/**
 * TagPicker — chips row + a "+" add control, for adding/removing tags on
 * an ingredient. Deliberately agnostic of *what* adding/removing a tag
 * actually does (`addFn`/`removeFn`) — same separation ImagePool draws
 * between the widget and its `uploadFn`/`deleteFn` — so the same
 * component covers both InlineEntityEditor's edit mode (assignment takes
 * effect immediately against a real ingredient id) and its create mode
 * (no id yet — the caller just resolves/holds the tag in memory until
 * the ingredient itself is created).
 *
 * The "+" sits at the end of the chips row, styled as one of them (a
 * pill, not a separate button style) — clicking it morphs that same slot
 * into a chip-styled text input with autocomplete suggestions below it.
 * Typing text that matches an existing tag suggests it (click or Enter to
 * add); typing something new offers "Crea "<text>"" — get-or-create by
 * name, never a separate deliberate creation step. Confirming (or
 * cancelling via Escape/clicking away) collapses the input back to a
 * fresh "+", ready to add another.
 */
import { Tag } from '../../api/types.js';
import { getTagColorHex, tagChipStyle } from '../../tags/tagColors.js';
import { tagGraphCache } from '../../tags/tagGraph.js';

export interface TagPickerOptions {
  container: HTMLElement;
  idPrefix?: string;
  /** Currently-assigned tags, shown as removable chips. */
  tags: Tag[];
  /** Every known tag, for autocomplete suggestions (fetch once via listTags()). */
  allTags: Tag[];
  /** Resolve (creating if needed) and add a tag by name. */
  addFn: (name: string) => Promise<Tag>;
  /** Remove an already-assigned tag by id. */
  removeFn: (tagId: string) => Promise<void>;
  /** Fired after every successful add/remove, with the resulting list. */
  onTagsChanged?: (tags: Tag[]) => void;
}

export class TagPicker {
  private container: HTMLElement;
  private options: TagPickerOptions;
  private idPrefix: string;
  private tags: Tag[];
  private adding: boolean = false;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;

  constructor(options: TagPickerOptions) {
    this.container = options.container;
    this.options = options;
    this.idPrefix = options.idPrefix || `tag-picker-${Date.now()}`;
    this.tags = [...options.tags];
  }

  render(): void {
    this.container.innerHTML = '';
    // Also drops the previous render's outside-click listener — otherwise
    // it leaks (same reasoning as ImagePool.disarmDelete()'s own comment):
    // never cleaned up on its own, and would keep firing against elements
    // this render() just replaced.
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-wrap items-center gap-1.5';

    for (const tag of this.tags) {
      wrapper.appendChild(this.buildChip(tag, false));
    }

    const impliedSlot = document.createElement('div');
    impliedSlot.className = 'contents';
    wrapper.appendChild(impliedSlot);

    const addSlot = document.createElement('div');
    addSlot.className = 'relative';
    wrapper.appendChild(addSlot);

    if (this.adding) {
      this.renderAddInput(addSlot);
    } else {
      this.renderAddButton(addSlot);
    }

    this.container.appendChild(wrapper);

    // Implied tags (via the tag-implication graph) render alongside the
    // explicit ones above, dimmed and with no remove control — but the
    // graph cache may not be loaded yet, so they're patched in
    // asynchronously rather than delaying the explicit chips above.
    // Guarded by isConnected: a later render() (add/remove, or the
    // add-input opening/closing) may already have replaced impliedSlot by
    // the time this resolves.
    tagGraphCache.ensureLoaded().then(() => {
      if (!impliedSlot.isConnected) return;
      for (const tag of tagGraphCache.getImpliedTags(this.tags)) {
        impliedSlot.appendChild(this.buildChip(tag, true));
      }
    }).catch((error) => {
      console.error('Failed to load tag implications:', error);
    });
  }

  private buildChip(tag: Tag, implied: boolean): HTMLElement {
    const chip = document.createElement('span');
    chip.className = 'inline-flex items-center gap-1 px-2.5 py-1 border rounded-full text-xs font-mono';
    chip.style.cssText = tagChipStyle(tag.color);
    if (implied) {
      chip.style.opacity = '0.55';
      chip.title = 'Implícita — no assignada directament';
    }

    const name = document.createElement('span');
    name.textContent = tag.name;
    chip.appendChild(name);

    // Removing an implied tag wouldn't do anything (the next render would
    // just re-derive it from whatever explicit tag still implies it), so
    // no remove control is offered for one.
    if (!implied) {
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'hover:text-[#ef4444] font-bold leading-none text-xs';
      removeBtn.style.color = getTagColorHex(tag.color);
      removeBtn.textContent = '×';
      removeBtn.title = `Elimina "${tag.name}"`;
      removeBtn.addEventListener('click', async () => {
        await this.options.removeFn(tag.id);
        this.tags = this.tags.filter(t => t.id !== tag.id);
        this.options.onTagsChanged?.([...this.tags]);
        this.render();
      });
      chip.appendChild(removeBtn);
    }

    return chip;
  }

  /** The "+" — styled as a chip-shaped pill, not a separate button look. */
  private renderAddButton(container: HTMLElement): void {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className =
      'inline-flex items-center justify-center px-2.5 py-1 rounded-full border border-[var(--accent-alpha-27)] text-[var(--accent)] text-xs font-mono hover:bg-[var(--accent-alpha-13)] transition-colors';
    btn.textContent = '+';
    btn.title = 'Afegeix una etiqueta';
    btn.addEventListener('click', () => {
      this.adding = true;
      this.render();
    });
    container.appendChild(btn);
  }

  /** The "+" morphed into a chip-styled input, with suggestions below it. */
  private renderAddInput(container: HTMLElement): void {
    const input = document.createElement('input');
    input.type = 'text';
    input.id = `${this.idPrefix}-input`;
    input.placeholder = 'Etiqueta...';
    input.className =
      'w-28 px-2.5 py-1 rounded-full border border-[var(--accent-alpha-40)] bg-[#0d1117] text-[var(--accent)] text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[var(--accent)]';
    container.appendChild(input);

    const suggestions = document.createElement('div');
    suggestions.className =
      'absolute z-10 left-0 w-40 mt-1 bg-[#1e1e2e] border border-[var(--accent-alpha-20)] rounded-md overflow-y-auto max-h-[160px] shadow-lg';
    suggestions.style.display = 'none';
    container.appendChild(suggestions);

    const cancel = (): void => {
      this.adding = false;
      this.render();
    };

    const renderSuggestions = (): void => {
      const q = input.value.trim().toLowerCase();
      suggestions.innerHTML = '';
      if (!q) {
        suggestions.style.display = 'none';
        return;
      }

      // Excludes both explicit tags and ones already implied by them —
      // adding an already-implied tag wouldn't visibly do anything, so it
      // isn't offered as if it would.
      const assignedIds = new Set([
        ...this.tags.map(t => t.id),
        ...tagGraphCache.getImpliedTags(this.tags).map(t => t.id),
      ]);
      const matches = this.options.allTags
        .filter(t => !assignedIds.has(t.id) && t.name.toLowerCase().includes(q))
        .slice(0, 8);
      const exactMatch = this.options.allTags.some(t => t.name.toLowerCase() === q)
        || this.tags.some(t => t.name.toLowerCase() === q);

      for (const tag of matches) {
        const row = document.createElement('div');
        row.className = 'px-3 py-1.5 text-sm text-[#e0e0e0] hover:bg-[var(--accent-alpha-13)] cursor-pointer';
        row.textContent = tag.name;
        row.addEventListener('click', () => { void confirmAdd(tag.name); });
        suggestions.appendChild(row);
      }

      if (!exactMatch) {
        const createRow = document.createElement('div');
        createRow.className =
          'px-3 py-1.5 text-sm text-[var(--accent)] hover:bg-[var(--accent-alpha-13)] cursor-pointer' +
          (matches.length > 0 ? ' border-t border-[var(--accent-alpha-13)]' : '');
        createRow.textContent = `Crea "${input.value.trim()}"`;
        createRow.addEventListener('click', () => { void confirmAdd(input.value); });
        suggestions.appendChild(createRow);
      }

      suggestions.style.display = (matches.length > 0 || !exactMatch) ? 'block' : 'none';
    };

    const confirmAdd = async (name: string): Promise<void> => {
      const trimmed = name.trim();
      if (!trimmed) {
        cancel();
        return;
      }
      // Already assigned or already implied (case-insensitive) — nothing
      // to do, just close.
      const alreadyEffective = [...this.tags, ...tagGraphCache.getImpliedTags(this.tags)];
      if (alreadyEffective.some(t => t.name.toLowerCase() === trimmed.toLowerCase())) {
        cancel();
        return;
      }
      const tag = await this.options.addFn(trimmed);
      this.tags.push(tag);
      this.adding = false;
      this.options.onTagsChanged?.([...this.tags]);
      this.render();
    };

    input.addEventListener('input', renderSuggestions);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void confirmAdd(input.value);
      } else if (e.key === 'Escape') {
        cancel();
      }
    });

    this.outsideClickHandler = (e: MouseEvent) => {
      if (!container.contains(e.target as Node)) {
        cancel();
      }
    };
    // Deferred, not attached immediately: the click that opened this
    // input (on the "+" button, just replaced by render()) is still
    // bubbling up toward document. Attaching synchronously would still
    // catch that same event once it arrives there — and since the old
    // button is by then a detached node, container.contains(e.target)
    // would read as "outside," cancelling the input before the user ever
    // sees it open. A macrotask delay lets this click finish dispatching
    // first, so only genuinely later clicks are ever considered.
    setTimeout(() => {
      if (this.outsideClickHandler) {
        document.addEventListener('click', this.outsideClickHandler);
      }
    }, 0);

    input.focus();
  }

  /** Clean up the outside-click listener when the host closes. */
  destroy(): void {
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
  }
}
